import dayjs from "dayjs";

/**
 * Converts a SCREAMING_SNAKE_CASE string into a human-readable sentence format,
 * capitalizing only the first word and transforming underscores to spaces.
 *
 * This is useful for displaying constants or enum values in a friendly format
 * (e.g., for dropdowns, labels, or messages).
 *
 * @param value - The SCREAMING_SNAKE_CASE string to convert.
 * @returns A sentence with the first word capitalized and the rest lowercase.
 *
 * @example
 * screamingSnakeToSentence("ROLE_MODEARTOR") // "Role moderator"
 * screamingSnakeToSentence("API_ERROR_OCCURRED") // "Api error occurred"
 */
export const screamingSnakeToSentence = <T extends string>(
  value: T,
): string => {
  return value
    .toLowerCase()
    .split("_")
    .map((word, i) =>
      i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");
};

/**
 * Capitalizes the first character of a string.
 * This is a simpler version than capitalizeFully - it only capitalizes the first letter
 * without changing the case of the rest of the string.
 *
 * @param str - The string to capitalize
 * @returns The string with the first character capitalized
 *
 * @example
 * capitalize("hello") // "Hello"
 * capitalize("HELLO") // "HELLO"
 * capitalize("") // ""
 */
export const capitalize = <T extends string>(str: T): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Checks if a value is empty. A value is considered empty if it is:
 * - null or undefined
 * - an empty string, array, or object
 * - a Map or Set with size 0
 * - a number that is NaN
 * - a boolean false
 * - a function (functions are never considered empty)
 *
 * @param value - The value to check
 * @returns True if the value is empty, false otherwise
 *
 * @example
 * isEmpty(null) // true
 * isEmpty(undefined) // true
 * isEmpty("") // true
 * isEmpty([]) // true
 * isEmpty({}) // true
 * isEmpty(new Map()) // true
 * isEmpty(new Set()) // true
 * isEmpty(NaN) // true
 * isEmpty(false) // true
 * isEmpty(0) // false
 * isEmpty("hello") // false
 * isEmpty([1, 2, 3]) // false
 * isEmpty({a: 1}) // false
 * isEmpty(() => {}) // false
 */
export const isEmpty = <T>(value: T): boolean => {
  if (value == null) return true;

  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === "boolean") {
    return value === false;
  }

  if (typeof value === "number") {
    return Number.isNaN(value);
  }

  if (typeof value === "function") {
    return false;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  return false;
};

/**
 * Formats a number with commas per thousand and an optional compact notation.
 * Currency display is not required in this version.
 *
 * @param value - The number to format.
 * @param compact - Whether to use compact notation (e.g., "1K" for 1000).
 * @returns The formatted string with commas added per thousand.
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1234567.89) // "1,234,567.89"
 * formatNumber(1234567.89, true) // "1.2M"
 */
export const formatNumber = (value: number, compact = false): string => {
  const formater = new Intl.NumberFormat("en-KE", {
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: value % 1 === 0 ? 0 : 2, // Show decimals only if needed
    maximumFractionDigits: 2, // Limit to 2 decimal places
  });

  return formater.format(value);
};

export const parseFormattedNumber = (value: string): number => {
  return Number(value.replace(/,/g, ""));
};

export const humanizeStatus = (text: string) =>
  text
    // snake_case → space
    .replace(/[_-]+/g, " ")
    // camelCase / PascalCase → space
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    // normalize spacing + capitalize words
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

export const parseDate = (str: string) => (str ? dayjs(str).toDate() : null);

export const now = new Date();

export const formatBytes = (bytes?: number, decimals = 2): string => {
  if (bytes === undefined || bytes === null) return "-";
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  return `${value.toFixed(decimals)} ${sizes[i]}`;
};
